import { useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Text,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Persona,
  PersonaSize,
  Link,
  Spinner,
  MessageBar,
  MessageBarType,
  ActionButton,
  PersonaPresence,
  Depths,
  Separator,
} from '@fluentui/react';
import { useParams } from 'react-router-dom/cjs/react-router-dom';
import MetaTable from '../components/MetaTable';
import { Chip } from './ProposalDashboard';

/**
 * ProjectDashboard
 * --------------------------------------
 * Props:
 *   - projectId (string | number) — required
 *
 * Expected API (any of these may be available; the component will try in order):
 *   1) GET /api/projectDashboard/:id/dashboard  -> {
 *        project: {...}, staff: [...], client: {...}, proposals: [...]
 *      }
 *   2) GET /api/projectDashboard/:id            -> { ...project }
 *      GET /api/projectDashboard/:id/staff      -> [ { id, name, role? } ]
 *      GET /api/projectDashboard/:id/proposals  -> [ { id, title, client_name?, year?, sharepoint_url? } ]
 *      GET /api/clients/:clientId       -> { id, name }
 */

export const styles = {
  root: { padding: 16, maxWidth: 1200, margin: '24px auto' },
  headerRow: { alignItems: 'center' },
  title: { fontSize: 24, fontWeight: 600 },
  subTitle: { color: '#666' },
  pillRow: { flexWrap: 'wrap' },
  pill: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    background: '#f3f2f1',
    marginRight: 8,
    marginBottom: 8,
    fontSize: 12,
  },
  sectionCard: {
    background: 'white',
    border: '1px solid #e1dfdd',
    borderRadius: 8,
    padding: 16,
    boxShadow: Depths.depth8
  },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' },
  wrapText: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    lineHeight: 1.45,
  },
};

export default function ProjectDashboard(props) {
  const params = useParams();
  const [data, setData] = useState({ project: null, staff: [], client: null, proposals: [], tags: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { projectId } = props.match.params;


  useEffect(() => {
    const { projectId } = params;
    let cancelled = false;
    async function fetchAll() {
      setLoading(true); setError(null);
      try {
        // Try the consolidated endpoint first
        const dash = await tryGet(`/api/projectDashboard/${projectId}/dashboard`);
        if (dash && dash.project) {
          if (!cancelled) setData(normalizeDashboard(dash));
          return;
        }
        // Fallback to individual endpoints
        const project = await mustGet(`/api/projectDashboard/${projectId}`);
        let client = null;
        if (project && (project.client_id || project.clientId)) {
          const cid = project.client_id ?? project.clientId;
          client = await tryGet(`/api/clients/${cid}`);
        }
        const staff = (await tryGet(`/api/projectDashboard/${projectId}/staff`)) || [];
        const proposals = (await tryGet(`/api/projectDashboard/${projectId}/proposals`)) || [];
        const tags = (await tryGet(`/api/projectDashboard/${projectId}/tags`) || [])
        if (!cancelled) setData(normalizeDashboard({ project, staff, client, proposals, tags }));
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, [projectId]);

  const { project, staff, client, proposals } = data;
  const { compass_pm_ref } = project || {};

  // console.log(data);

  const timeRange = useMemo(() => formatTimeRange(project), [project]);
  const budget = useMemo(() => pickFirst(project, ['billable_amount', 'budget', 'total_budget']), [project]);
  const description = useMemo(() => pickFirst(project, ['canonical_description', 'summary', 'description']), [project]);
  const title = useMemo(() => pickFirst(project, ['title', 'name', 'project_number']) || `Project ${projectId}`, [project, projectId]);
  const subjectTags = useMemo(() => buildSubjectTags(project), [project]);

  const proposalColumns = [
    { key: 'col1', name: 'Title', fieldName: 'title', minWidth: 200, isResizable: true, onRender: (item) => <Link href={`/proposals/${item.id}`}>{item.title}</Link>},
    { key: 'col2', name: 'Client', fieldName: 'client_name', minWidth: 120, isResizable: true },
    { key: 'col3', name: 'Year', fieldName: 'year', minWidth: 60, maxWidth: 80, isResizable: true },
  ];

  if (loading) return <Spinner label="Loading project..." />;
  if (error) return <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>;
  if (!project) return <MessageBar>Project not found.</MessageBar>;

  return (
    <Stack styles={{ root: styles.root }} tokens={{ childrenGap: 16 }}>
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center" styles={{ root: styles.headerRow }}>
        <Stack>
          <Text styles={{ root: styles.title }}>{title}</Text>
          <Text styles={{ root: styles.subTitle }}>
            {client?.name ? `${client.name}` : (project.client_friendly_name || '')}
            {timeRange ? (client?.name ? ` • ${timeRange}` : timeRange) : null}
          </Text>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          {project.folder_link ? (
            <ActionButton text='Open in SharePoint' iconProps={{ iconName: 'SharepointAppIcon16' }} title="Open SharePoint" ariaLabel="Open SharePoint" onClick={() => window.open(project.folder_link, '_blank', 'noreferrer')} />
          ) : null}
          
          <ActionButton text='Edit Project' iconProps={{ iconName: 'Edit' }} title="Edit Project" ariaLabel="Edit Project" onClick={() => window.open(`${props.match.url}/edit`, '_self')} />
        </Stack>
      </Stack>

      {/* Description & Meta */}
      <Stack tokens={{childrenGap: 16}}>
        <Stack.Item grow={1} styles={{ root: styles.sectionCard }}>
          <Stack>
            <Text variant="xLarge">Description</Text>
            <Separator />
            <Text>{description}</Text>
          </Stack>
        </Stack.Item>

        <Stack.Item grow={1} styles={{ root: { ...styles.sectionCard }}} tokens={{ childrenGap: 8 }}>
          <Text variant="xLarge">At a glance</Text>
          <Separator />
          <MetaTable 
            borderless
            items={[
            {label: "Timeframe", text: timeRange || '-'},
            {
              label: "Tags", 
              text: data.tags.map(t => t.name).join(", "),
              onRender: () => {
                return <Stack wrap horizontal tokens={{childrenGap: 4}}>
                  {data.tags.map(t => {
                    return <Stack.Item key={t.name}>
                      <Chip>{t.name}</Chip>
                    </Stack.Item>
                  })}
                </Stack>
              }
            },
            {label: "Billable Amount", text: formatCurrency(budget) || '-'},
            {label: "Client", text: client?.name || project.client_friendly_name || '-'},
            {label: "Project #", text: project.project_number || '-'},
            {label: "Status", text: project.status || '-'}
          ]} />
        </Stack.Item>
      </Stack>

      {/* People */}
      <Stack styles={{ root: styles.sectionCard }} tokens={{ childrenGap: 8 }}>
        <Text variant="xLarge">Compass Team</Text>
        <Separator />
        {staff?.length ? (
          <Stack horizontal wrap tokens={{ childrenGap: 12 }}>
            {staff.map((p) => (
              <Stack.Item key={p.id}>
                <Persona 
                  styles={{root: {cursor: "pointer"}}}
                  presence={compass_pm_ref === p.name ? PersonaPresence.online : PersonaPresence.none}
                  text={p.name || p.full_name || `Person ${p.id}`} 
                  onClick={() => props.history.push(`/people/${p.id}`)}
                  secondaryText={p.role || p.title || ''} 
                  size={PersonaSize.size40} />
              </Stack.Item>
            ))}
          </Stack>
        ) : (
          <Text>No linked staff found.</Text>
        )}
      </Stack>

      {/* Proposals */}
      <Stack styles={{ root: styles.sectionCard }} tokens={{ childrenGap: 8 }}>
        <Text variant="xLarge">Mentioned in Proposals</Text>
        <Separator />
        {proposals?.length ? (
          <DetailsList
            items={proposals}
            columns={proposalColumns}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            styles={{ root: { overflowX: 'auto' } }}
          />
        ) : (
          <Text>No linked proposals found.</Text>
        )}
      </Stack>
    </Stack>
  );
}

function MetaRow({ label, value, mono }) {
  return (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="baseline">
      <Text styles={{ root: { width: 100, color: '#605e5c' } }}>{label}</Text>
      <Text styles={{ root: { ...(mono ? styles.mono : {}), ...styles.wrapText } }}>{value}</Text>
    </Stack>
  );
}

// ---------------- Helpers ----------------
async function tryGet(url) {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
}
async function mustGet(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${r.status} ${r.statusText} — ${url}`);
  return r.json();
}

function normalizeDashboard(input) {
  const project = input?.project || input;
  const client = input?.client || null;
  const staff = Array.isArray(input?.staff) ? input.staff : [];
  const proposals = Array.isArray(input?.proposals) ? input.proposals : [];
  const tags = input.tags || [];

  // Ensure reasonable shape for proposals
  const normProposals = proposals.map((p) => ({
    id: p.id,
    title: pickFirst(p, ['title', 'name']) || `(Proposal ${p.id})`,
    client_name: p.client_name || p.client || null,
    year: p.year || p.date_submitted_year || (p.date_submitted ? new Date(p.date_submitted).getFullYear() : undefined),
    sharepoint_url: p.sharepoint_url || p.url || null
  }));

  return { project, client, staff, proposals: normProposals, tags };
}

function formatTimeRange(project) {
  if (!project) return '';
  const start = pickDate(project, ['summary_start_date', 'start_date', 'date_started']);
  const end = pickDate(project, ['summary_end_date', 'end_date', 'date_completed']);
  const year = project.year || project.start_year || project.date_year;

  if (start && end) return `${start} – ${end}`;
  if (start) return `${start} – present`;
  if (year) return String(year);
  return '';
}

function pickDate(obj, keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (!v) continue;
    const d = new Date(v);
    if (!isNaN(d)) return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
  }
  return null;
}

function pickFirst(obj, keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] != null && obj[k] !== '') return obj[k];
  }
  return undefined;
}

function buildSubjectTags(project) {
  if (!project) return [];
  // Prefer comma- or semicolon-separated fields if present
  const raw = pickFirst(project, ['topics', 'subject_matter', 'keywords', 'tags', 'search_text']);
  if (!raw) return [];
  return String(raw)
    .split(/[;,]/g)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 20);
}

function formatCurrency(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
  } catch {
    return `$${Math.round(n).toLocaleString()}`;
  }
}
